import axios from "axios";
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { resetPhotosGallery } from "../../../features/gallery/gallerySlice";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import { ReactComponent as Next } from "../../../resources/caret-right-fill.svg";
import { ReactComponent as Prev } from "../../../resources/caret-left-fill.svg";
import { ReactComponent as Do } from "../../../resources/do.svg";
import PhotoThumbnail from "./PhotoThumbnail";
import Notification from "../dialog/Notification";
import { BACKEND_URL } from "constants";

const PhotoStepper = ({ photos = [], requestedPhoto, returnPage = 1 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [startIndex, setStartIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [success, setSuccess] = useState(true);
  const [selected, setSelected] = useState(undefined);

  // Track how many items to show based on screen width
  const [itemsPerPage, setItemsPerPage] = useState(3);

  // Update itemsPerPage on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1); // Mobile: show 1 item
      } else if (window.innerWidth < 992) {
        setItemsPerPage(2); // Tablet: show 2 items
      } else {
        setItemsPerPage(3); // Desktop: show 3 items
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Make sure startIndex doesn't exceed bounds if window is resized
  useEffect(() => {
    if (startIndex + itemsPerPage > photos.length && photos.length > 0) {
      setStartIndex(Math.max(0, photos.length - itemsPerPage));
    }
  }, [itemsPerPage, photos.length, startIndex]);

  const visiblePhotos = useMemo(
    () => photos.slice(startIndex, startIndex + itemsPerPage),
    [photos, startIndex, itemsPerPage]
  );

  const handleNext = () => {
    if (startIndex + itemsPerPage < photos.length) {
      setStartIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (startIndex > 0) {
      setStartIndex((prev) => prev - 1);
    }
  };

  const handleSendRequest = (photo, requestedPhoto) => {
    if (!photo || !requestedPhoto) return;

    const payload = {
      sender_user_id: photo["user_id"],
      sender_photo_id: photo["photo_id"],
      receiver_user_id: requestedPhoto["user_id"],
      receiver_photo_id: requestedPhoto["photo_id"],
      accepted: null,
      origin: "gallery",
      type: "do",
    };

    axios
      .post(BACKEND_URL + "/request/create", payload)
      .then(() => {
        dispatch(resetPhotosGallery());
        setSuccess(true);
        setShowNotification(true);
      })
      .catch((error) => {
        setSuccess(false);
        setShowNotification(true);
        console.error(error);
      });
  };

  const onDoClick = (photo) => {
    setSelected(photo);
    setShow(true);
  };

  const onClose = () => {
    navigate("/gallery", { state: { targetPage: returnPage } });
  };

  return (
    <div className="photo-stepper-container">
      <ConfirmationDialog
        text="Do you do the same?"
        show={show}
        setShow={setShow}
        onConfirm={() => handleSendRequest(selected, requestedPhoto)}
      />

      <Notification
        text={
          success
            ? "The user of the requested photo has been notified! You can send a message after the request is accepted!"
            : "Photo request already exists!"
        }
        variant={success ? "secondary" : "danger"}
        show={showNotification}
        setShow={setShowNotification}
        onClose={onClose}
      />

      <div className="photo-stepper">
        <Button
          variant="light"
          className="stepper-arrow-button"
          onClick={handlePrev}
          disabled={startIndex === 0}
          aria-label="Previous photos"
        >
          <Prev className="stepper-arrow-icon" />
        </Button>

        <div className="photo-wrapper">
          {visiblePhotos.map((photo) => (
            <div className="gallery-item" key={photo["photo_id"]}>
              <PhotoThumbnail
                src={`data:image/jpeg;base64,${photo["photo"]}`}
                photoId={photo["photo_id"]}
                thumbnailClass="shared-photo-thumbnail stepper-thumbnail"
              />

              <Button
                variant="outline-success"
                className="do-button"
                onClick={() => onDoClick(photo)}
                aria-label="Send do request"
              >
                <Do className="do-icon" />
              </Button>
            </div>
          ))}
        </div>

        <Button
          variant="light"
          className="stepper-arrow-button"
          onClick={handleNext}
          disabled={startIndex + itemsPerPage >= photos.length}
          aria-label="Next photos"
        >
          <Next className="stepper-arrow-icon" />
        </Button>
      </div>
    </div>
  );
};

export default PhotoStepper;